<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  header: { type: Object, default: () => ({ format: 'NONE' }) },
  body: { type: String, default: '' },
  footer: { type: String, default: '' },
  buttons: { type: Array, default: () => [] },
  headerPreviewUrl: { type: String, default: '' },
});

const { t } = useI18n();

const isDark = ref(document.body.classList.contains('dark'));

let observer;
onMounted(() => {
  observer = new MutationObserver(() => {
    isDark.value = document.body.classList.contains('dark');
  });
  observer.observe(document.body, {
    attributes: true,
    attributeFilter: ['class'],
  });
});
onUnmounted(() => observer?.disconnect());

const bodyWithPlaceholders = computed(() =>
  (props.body || '').replace(
    /\{\{(\d+)\}\}/g,
    (_, n) =>
      `<span class="text-n-blue-11 bg-n-blue-3 px-1 rounded">{{${n}}}</span>`
  )
);

const carouselButton = computed(() =>
  props.buttons.find(b => b.type === 'CAROUSEL')
);

const carouselCards = computed(() => carouselButton.value?.cards || []);

const visibleButtons = computed(() => {
  if (carouselButton.value) return [];
  return props.buttons
    .filter(b => (b.text || '').trim().length > 0)
    .slice(0, 3);
});

const theme = computed(() =>
  isDark.value
    ? {
        chat: '#0b141a',
        bubble: '#202c33',
        text: '#e9edef',
        secondary: '#8696a0',
        placeholder: '#2a3942',
        divider: '#2a3942',
        hover: '#2a3942',
      }
    : {
        chat: '#e5ddd5',
        bubble: '#ffffff',
        text: '#111b21',
        secondary: '#667781',
        placeholder: '#f0f2f5',
        divider: '#e9edef',
        hover: '#f5f6f6',
      }
);
</script>

<template>
  <div class="flex flex-col items-center justify-start gap-2 w-full">
    <span class="text-xs font-medium text-n-slate-10">{{
      t('FLOW_BUILDER.TEMPLATE_PREVIEW.TITLE')
    }}</span>
    <div
      class="rounded-2xl p-6 w-full max-w-sm flex flex-col gap-2 shadow-inner transition-colors duration-200"
      :style="{ backgroundColor: theme.chat }"
    >
      <div
        class="rounded-lg shadow-md overflow-hidden flex flex-col transition-colors duration-200"
        :style="{ backgroundColor: theme.bubble, color: theme.text }"
      >
        <div
          v-if="header.format === 'IMAGE' && headerPreviewUrl"
          class="w-full aspect-video overflow-hidden"
          :style="{ backgroundColor: theme.placeholder }"
        >
          <img
            :src="headerPreviewUrl"
            alt="header"
            class="w-full h-full object-cover"
          />
        </div>
        <div
          v-else-if="header.format === 'IMAGE'"
          class="w-full aspect-video flex items-center justify-center text-xs"
          :style="{
            backgroundColor: theme.placeholder,
            color: theme.secondary,
          }"
        >
          {{ t('FLOW_BUILDER.TEMPLATE_PREVIEW.HEADER_IMAGE') }}
        </div>
        <div
          v-else-if="header.format === 'PRODUCT'"
          class="w-full aspect-square max-h-40 flex flex-col items-center justify-center gap-1 text-xs"
          :style="{
            backgroundColor: theme.placeholder,
            color: theme.secondary,
          }"
        >
          <span class="i-lucide-package size-8 opacity-50" />
          <span>{{ t('FLOW_BUILDER.TEMPLATE_PREVIEW.PRODUCT_HEADER') }}</span>
        </div>
        <div
          v-if="header.format === 'TEXT' && header.text"
          class="px-3 pt-3 text-sm font-bold"
        >
          {{ header.text }}
        </div>

        <div
          v-if="body"
          class="px-3 py-2 text-sm whitespace-pre-wrap break-words"
        >
          <span v-html="bodyWithPlaceholders" />
        </div>
        <div
          v-else
          class="px-3 py-2 text-xs italic"
          :style="{ color: theme.secondary }"
        >
          {{ t('FLOW_BUILDER.TEMPLATE_PREVIEW.BODY_PLACEHOLDER') }}
        </div>

        <div
          v-if="footer"
          class="px-3 pb-2 text-xs"
          :style="{ color: theme.secondary }"
        >
          {{ footer }}
        </div>

        <div
          class="px-3 pb-2 text-[10px] text-right"
          :style="{ color: theme.secondary }"
        >
          {{
            new Date().toLocaleTimeString('pt-BR', {
              hour: '2-digit',
              minute: '2-digit',
            })
          }}
        </div>

        <div
          v-if="visibleButtons.length"
          class="divide-y"
          :style="{
            borderTop: `1px solid ${theme.divider}`,
            borderColor: theme.divider,
          }"
        >
          <button
            v-for="(btn, idx) in visibleButtons"
            :key="idx"
            type="button"
            class="w-full px-3 py-2 flex items-center justify-center gap-1.5 text-[#00a884] text-sm font-medium transition-colors"
            :style="{ borderColor: theme.divider }"
            @mouseover="
              $event.currentTarget.style.backgroundColor = theme.hover
            "
            @mouseleave="
              $event.currentTarget.style.backgroundColor = 'transparent'
            "
          >
            <span
              :class="{
                'i-lucide-external-link': btn.type === 'URL',
                'i-lucide-phone': btn.type === 'PHONE_NUMBER',
                'i-lucide-reply': btn.type === 'QUICK_REPLY',
                'i-lucide-store': btn.type === 'CATALOG',
                'i-lucide-package': btn.type === 'SPM',
                'i-lucide-layout-list': btn.type === 'MPM',
              }"
              class="size-3.5"
            />
            {{ btn.text }}
          </button>
        </div>
      </div>

      <!-- Carousel cards preview -->
      <div
        v-if="carouselCards.length"
        class="flex gap-2 overflow-x-auto pb-1 w-full max-w-sm"
      >
        <div
          v-for="(card, i) in carouselCards"
          :key="i"
          class="flex-none w-36 rounded-lg shadow-md overflow-hidden flex flex-col transition-colors duration-200"
          :style="{ backgroundColor: theme.bubble, color: theme.text }"
        >
          <div
            class="w-full aspect-square flex flex-col items-center justify-center gap-1 text-xs"
            :style="{
              backgroundColor: theme.placeholder,
              color: theme.secondary,
            }"
          >
            <span class="i-lucide-package size-5 opacity-50" />
            <span>{{ t('FLOW_BUILDER.TEMPLATE_PREVIEW.PRODUCT_HEADER') }}</span>
          </div>
          <div
            v-if="card.body"
            class="px-2 py-1.5 text-xs whitespace-pre-wrap break-words leading-tight"
          >
            {{ card.body }}
          </div>
          <div
            v-else
            class="px-2 py-1.5 text-xs italic"
            :style="{ color: theme.secondary }"
          >
            {{
              t('WHATSAPP_TEMPLATES.CREATE.BUTTON_TYPES.CAROUSEL_CARD_BODY', {
                n: i + 1,
              })
            }}
          </div>
          <div
            class="border-t px-2 py-1.5 flex items-center justify-center gap-1 text-[#00a884] text-xs font-medium"
            :style="{ borderColor: theme.divider }"
          >
            <span class="i-lucide-package size-3 opacity-70" />
            {{ t('FLOW_BUILDER.TEMPLATE_PREVIEW.PRODUCT_HEADER') }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
